import { useEffect, useState, useMemo } from "react";
import Modal from "../common/Modal";
import Button from "../common/Button";
import roleMenuService from "../../services/roleMenuService";
import ErrorAlert from "../common/ErrorAlert";

const PermissionRadio = ({ name, label, checked, onChange, disabled }) => (
  <label className={`flex items-center gap-2 cursor-pointer select-none ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}>
    <input
      type="radio"
      name={name}
      checked={checked}
      onChange={onChange}
      disabled={disabled}
      className="h-4 w-4 rounded border-slate-300 text-cyan-600 focus:ring-cyan-500 dark:border-slate-700 dark:bg-slate-800"
    />
    <span className="text-xs font-medium text-slate-700 dark:text-slate-300">{label}</span>
  </label>
);

const RolePermissionsModal = ({ isOpen, onClose, role }) => {
  const [menus, setMenus] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [permissionState, setPermissionState] = useState({});

  const allSelected = useMemo(() => {
    if (Object.keys(permissionState).length === 0) return false;
    return Object.values(permissionState).every((perms) => perms.isRead);
  }, [permissionState]);

  useEffect(() => {
    if (isOpen && role?.id) {
      loadPermissions();
    }
  }, [isOpen, role]);

  const loadPermissions = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await roleMenuService.getMenusByRole(role.id);
      // data is expected to be a list of menus with permissions
      setMenus(data || []);
      
      // Initialize local state for toggling
      const initialState = {};
      data.forEach((menu) => {
        const p = menu.permissions || {};
        initialState[menu.menuId || menu.id] = {
          isRead: Boolean(p.isRead),
        };
      });
      setPermissionState(initialState);
    } catch (err) {
      setError("Failed to load permissions. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggle = async (menuId, value) => {
    const currentPerms = permissionState[menuId];
    if (!currentPerms) return;

    const newPerms = { isRead: value };

    // Update local state immediately for snappy UI
    setPermissionState((prev) => ({
      ...prev,
      [menuId]: newPerms,
    }));

    try {
      setError(null);
      await roleMenuService.updateRoleMenuPermissions(role.id, menuId, newPerms);
    } catch (err) {
      setError("Failed to update view permission.");
      // Revert state on failure
      setPermissionState((prev) => ({
        ...prev,
        [menuId]: currentPerms,
      }));
    }
  };

  const handleGlobalToggle = async (value) => {
    const previousState = { ...permissionState };
    const newState = {};
    Object.keys(permissionState).forEach((menuId) => {
      newState[menuId] = {
        isRead: value,
      };
    });

    setPermissionState(newState);

    try {
      setError(null);
      const permissionsData = Object.entries(newState).map(([menuId, perms]) => ({
        menuId: Number(menuId),
        isRead: Boolean(perms?.isRead),
      }));

      await roleMenuService.updateBulkRoleMenuPermissions(role.id, permissionsData);
    } catch (err) {
      setError("Failed to update all permissions.");
      setPermissionState(previousState);
    }
  };

  const menuTree = useMemo(() => {
    if (!menus.length) return [];
    
    const map = {};
    menus.forEach((menu) => {
      map[menu.menuId || menu.id] = { ...menu, children: [] };
    });
    
    const tree = [];
    menus.forEach((menu) => {
      const id = menu.menuId || menu.id;
      const parentId = menu.parentId || menu.parentMenuId;
      if (parentId && map[parentId]) {
        map[parentId].children.push(map[id]);
      } else {
        tree.push(map[id]);
      }
    });
    return tree;
  }, [menus]);

  const renderMenuRow = (menu, level = 0) => {
    const id = menu.menuId || menu.id;
    const perms = permissionState[id] || { isRead: false };

    return (
      <div key={id} className="flex flex-col">
        <div className={`flex items-center justify-between border-b border-slate-100 py-3 dark:border-slate-800 ${level > 0 ? "ml-6" : ""}`}>
          <div className="flex items-center gap-3">
            <span className="text-sm font-semibold text-slate-800 dark:text-slate-200">
              {menu.menuName || menu.title || "Unnamed Menu"}
            </span>
          </div>
          <div className="flex items-center gap-4">
            <PermissionRadio
              name={`menu-view-${id}`}
              label="View"
              checked={perms.isRead}
              onChange={() => handleToggle(id, true)}
            />
            <PermissionRadio
              name={`menu-view-${id}`}
              label="Unview"
              checked={!perms.isRead}
              onChange={() => handleToggle(id, false)}
            />
          </div>
        </div>
        {menu.children && menu.children.length > 0 && (
          <div className="flex flex-col">
            {menu.children.map((child) => renderMenuRow(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      title={`Permissions for: ${role?.roleName || "Role"}`}
    >
      <div className="flex flex-col max-h-[70vh]">
        {error && <div className="mb-4"><ErrorAlert message={error} /></div>}
        
        {!isLoading && menus.length > 0 && (
          <div className="mb-2 flex justify-end">
            <button
              onClick={() => handleGlobalToggle(!allSelected)}
              className="text-[11px] font-bold text-cyan-600 hover:text-cyan-700 dark:text-cyan-400 uppercase tracking-widest bg-cyan-50 dark:bg-cyan-500/10 px-2 py-1 rounded"
            >
              {allSelected ? "Set All Unview" : "Set All View"}
            </button>
          </div>
        )}

        <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar">
          {isLoading ? (
            <div className="flex justify-center py-10">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-cyan-500 border-t-transparent"></div>
            </div>
          ) : menus.length === 0 ? (
            <p className="text-center py-10 text-slate-500 dark:text-slate-400">No menus found.</p>
          ) : (
            <div className="divide-y divide-slate-100 dark:divide-slate-800">
              {menuTree.map(menu => renderMenuRow(menu))}
            </div>
          )}
        </div>

        <div className="mt-6 flex items-center justify-end border-t border-slate-100 pt-4 dark:border-slate-800">
          <Button onClick={onClose} disabled={isLoading}>
            Done
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default RolePermissionsModal;
